import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye, Target, ArrowLeft, Clock, FileText, BookOpen, Lightbulb,
  Award, MessageSquare, Calendar, User, Paperclip, X
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import AddEditLearningSourceModal from './components/AddEditLearningSourceModal';

// Mock development objective data
const mockObjective = {
  id: 'obj_001',
  focus: 'Develop advanced data visualization skills for customer-facing dashboards',
  description: 'Build expertise in creating intuitive, accessible data visualizations that help customers make informed decisions. Focus on modern charting libraries and UX best practices.',
  linkedRole: { roleName: 'Senior Product Designer', effectiveDates: 'Jan 2024 - Present' },
  horizon: '6 months',
  supporter: { name: 'Michael Chen', title: 'Design Manager' },
  createdDate: '2024-06-15T10:00:00Z',
  currentPhase: 'Active Development',
  milestones: [
    { id: 'm1', label: 'Objective Created', status: 'completed', date: '2024-06-15' },
    { id: 'm2', label: 'Initial Evidence', status: 'completed', date: '2024-07-01' },
    { id: 'm3', label: 'Mid-Point Review', status: 'in-progress', date: '2024-09-15' },
    { id: 'm4', label: 'Final Assessment', status: 'pending', date: '2024-12-15' },
  ],
  evidenceSummary: '8 items submitted, 3 manager comments',
  lastDiscussed: '2024-11-20T14:00:00Z',
};

const mockTimelineItems = [
  { id: 'tl_001', type: 'milestone', title: 'Objective Created', date: '2024-06-15T10:00:00Z', description: 'Development objective established with 6-month horizon', icon: 'Target', color: 'purple' },
  { id: 'tl_002', type: 'evidence', title: 'Completed D3.js Advanced Course', date: '2024-07-10T09:00:00Z', description: 'Certificate of completion for advanced data visualization course', attachments: [{ name: 'certificate.pdf', size: '245 KB' }], icon: 'Award', color: 'blue' },
  { id: 'tl_003', type: 'feedback', title: 'Manager Feedback', date: '2024-07-25T14:30:00Z', description: 'Great progress on the course! Looking forward to seeing these skills applied in upcoming projects.', author: 'Michael Chen', icon: 'MessageSquare', color: 'green' },
  { id: 'tl_004', type: 'reflection', title: 'Employee Reflection', date: '2024-08-15T16:00:00Z', description: 'Applied new charting techniques to customer dashboard prototype. Received positive feedback from UX team on accessibility improvements.', icon: 'BookOpen', color: 'orange' },
  { id: 'tl_005', type: 'evidence', title: 'Dashboard Prototype Presentation', date: '2024-09-05T11:00:00Z', description: 'Presented new data visualization approach to product team', attachments: [{ name: 'presentation.pptx', size: '3.2 MB' }], icon: 'Award', color: 'blue' },
  { id: 'tl_006', type: 'checkin', title: 'Check-in Discussion', date: '2024-11-20T14:00:00Z', description: 'Discussed progress and identified areas for continued focus in final months', icon: 'Calendar', color: 'purple' },
];

const mockEvidenceItems = [
  { id: 'ev_001', title: 'D3.js Advanced Course Certificate', category: 'Learning & Development', submittedDate: '2024-07-10', managerComment: 'Excellent foundation for upcoming projects', tags: ['Technical Skills', 'Certification'] },
  { id: 'ev_002', title: 'Customer Dashboard Prototype', category: 'Project Work', submittedDate: '2024-09-05', managerComment: 'Strong application of learned concepts', tags: ['Applied Learning', 'Customer Impact'] },
  { id: 'ev_003', title: 'Accessibility Audit Results', category: 'Quality & Standards', submittedDate: '2024-10-12', managerComment: null, tags: ['Accessibility', 'Best Practices'] },
];

const mockReflections = [
  { id: 'ref_001', date: '2024-08-15', prompt: 'What have you tried this month?', response: 'Applied new charting techniques to customer dashboard prototype. Experimented with different color schemes for better data distinction.', managerResponse: 'Great to see immediate application of your learning.' },
  { id: 'ref_002', date: '2024-09-20', prompt: 'What challenges did you encounter?', response: 'Balancing visual appeal with performance was challenging when working with large datasets.', managerResponse: 'Performance optimization is a critical skill. Let\'s discuss strategies in our next check-in.' },
  { id: 'ref_003', date: '2024-10-18', prompt: 'What changed in your approach?', response: 'Started involving stakeholders earlier in the design process to validate visualization choices.', managerResponse: null },
];

const getIconComponent = (iconName: string) => {
  const icons: Record<string, typeof Target> = { Target, Award, MessageSquare, BookOpen, Calendar };
  return icons[iconName] || Target;
};

const getColorClass = (color: string) => {
  const colors: Record<string, string> = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
  };
  return colors[color] || colors.purple;
};

const getMilestoneStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'bg-green-500';
    case 'in-progress': return 'bg-blue-500 animate-pulse';
    case 'pending': return 'bg-gray-300';
    default: return 'bg-gray-300';
  }
};

export default function DevelopmentObjectiveDetail() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('timeline');
  const [isLearningSourceModalOpen, setIsLearningSourceModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<{ title: string; type: string; url?: string; notes?: string } | null>(null);

  const handleAddLearningSource = () => {
    setEditingSource(null);
    setIsLearningSourceModalOpen(true);
  };

  const handleCloseLearningSourceModal = () => {
    setIsLearningSourceModalOpen(false);
    setEditingSource(null);
  };

  const handleSubmitLearningSource = (source: { title: string; type: string; url?: string; notes?: string }) => {
    console.log('Submitted learning source:', source);
    handleCloseLearningSourceModal();
  };

  const objective = mockObjective;

  const tabs = [
    { id: 'timeline', label: 'Timeline', icon: Clock, count: mockTimelineItems.length },
    { id: 'evidence', label: 'Evidence', icon: FileText, count: mockEvidenceItems.length },
    { id: 'reflections', label: 'Reflections', icon: BookOpen, count: mockReflections.length },
    { id: 'learning', label: 'Learning Sources', icon: Lightbulb, count: undefined },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Eye className="w-6 h-6" />}
        title="Development Objective Detail"
        description="Development Objective Detail is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-cyan-500"
        gradientTo="to-blue-500"
      />

      {/* Header */}
      <div className="mb-6">
        <button
          
          className="flex items-center space-x-2 text-sm text-muted-foreground mb-4"
         
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('Back to Development')}</span>
        </button>
        <h1 className="text-3xl font-bold text-foreground">{t('Development Objective')}</h1>
        <p className="text-muted-foreground mt-2">{t('Track progress, evidence, and reflections')}</p>
      </div>

      {/* Objective Summary Card */}
      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground mb-1">{objective.focus}</h2>
            <p className="text-sm text-muted-foreground mb-2">{objective.description}</p>
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <span>{objective.linkedRole.roleName}</span>
              <span>|</span>
              <span>{t('Horizon')}: {objective.horizon}</span>
              <span>|</span>
              <span>{t('Phase')}: {objective.currentPhase}</span>
            </div>
          </div>
        </div>

        {/* Milestones */}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-3">{t('Milestones')}</p>
          <div className="flex items-center space-x-2">
            {objective.milestones.map((milestone, idx) => (
              <div key={milestone.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${getMilestoneStatusColor(milestone.status)}`} />
                  <span className="text-xs text-muted-foreground mt-1 whitespace-nowrap">{milestone.label}</span>
                </div>
                {idx < objective.milestones.length - 1 && (
                  <div className="w-12 h-0.5 bg-border mx-2 mt-[-12px]" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Supporter */}
        <div className="mt-4 pt-4 border-t border-border flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-medium">
            {objective.supporter.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{objective.supporter.name}</p>
            <p className="text-xs text-muted-foreground">{objective.supporter.title}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{t(tab.label)}</span>
              {tab.count !== undefined && (
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${
                  activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {mockTimelineItems.map((item) => {
              const IconComp = getIconComponent(item.icon);
              return (
                <div key={item.id} className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getColorClass(item.color)}`}>
                    <IconComp className="w-5 h-5" />
                  </div>
                  <div className="flex-1 bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-foreground">{item.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    {item.author && (
                      <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-2">
                        <User className="w-3 h-3" />
                        <span>{item.author}</span>
                      </div>
                    )}
                    {item.attachments && item.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.attachments.map((file, idx) => (
                          <div key={idx} className="flex items-center space-x-2 p-2 bg-muted rounded-lg text-sm">
                            <Paperclip className="w-3 h-3 text-muted-foreground" />
                            <span className="text-foreground">{file.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{file.size}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Evidence Tab */}
        {activeTab === 'evidence' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{t('Evidence Items')}</h3>
              <button
                
                className="flex items-center space-x-2 px-3 py-1.5 text-xs bg-primary text-white rounded-lg"
               
              >
                <FileText className="w-3 h-3" />
                <span>{t('Submit Evidence')}</span>
              </button>
            </div>
            {mockEvidenceItems.map((item) => (
              <div key={item.id} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{item.title}</h4>
                    <p className="text-xs text-muted-foreground">{item.category} | {new Date(item.submittedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">{tag}</span>
                  ))}
                </div>
                {item.managerComment && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <p className="text-xs font-medium text-green-700 mb-1">{t('Manager Comment')}</p>
                    <p className="text-sm text-foreground">{item.managerComment}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Reflections Tab */}
        {activeTab === 'reflections' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{t('Reflections')}</h3>
              <button
                
                className="flex items-center space-x-2 px-3 py-1.5 text-xs bg-primary text-white rounded-lg"
               
              >
                <BookOpen className="w-3 h-3" />
                <span>{t('Add Reflection')}</span>
              </button>
            </div>
            {mockReflections.map((refl) => (
              <div key={refl.id} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {new Date(refl.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <p className="text-xs font-medium text-muted-foreground mb-1">{refl.prompt}</p>
                <p className="text-sm text-foreground mb-3">{refl.response}</p>
                {refl.managerResponse && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-blue-700 mb-1">{t('Manager Response')}</p>
                    <p className="text-sm text-foreground">{refl.managerResponse}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Learning Sources Tab */}
        {activeTab === 'learning' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{t('Learning Sources')}</h3>
              <button
                onClick={handleAddLearningSource}
                className="flex items-center space-x-2 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Lightbulb className="w-3 h-3" />
                <span>{t('Add Source')}</span>
              </button>
            </div>
            <div className="text-center py-8">
              <Lightbulb className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('No learning sources added yet. Add books, articles, or videos to support this objective.')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Learning Source Modal */}
      <AddEditLearningSourceModal
        isOpen={isLearningSourceModalOpen}
        onClose={handleCloseLearningSourceModal}
        onSubmit={handleSubmitLearningSource}
        editingSource={editingSource}
        userRole="employee"
      />
    </div>
  );
}
